import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { ChronologyFormValues } from "../helpers/chronologyRequest";
import { ChronologyEventEditor, EditorStep } from "./ChronologyEventEditor";

interface ChronologyEventEditorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "add" | "edit";
  initialValues?: ChronologyFormValues;
  isSaving: boolean;
  isLoadingSuggestion?: boolean;
  onSubmit: (values: ChronologyFormValues) => void;
}

export const ChronologyEventEditorModal: FC<
  ChronologyEventEditorModalProps
> = ({
  open,
  onOpenChange,
  mode,
  initialValues,
  isSaving,
  isLoadingSuggestion,
  onSubmit,
}) => {
  const intl = useIntl();
  const [step, setStep] = useState(EditorStep.EVENT_DETAILS);
  const titleId =
    mode === "add" ? "chronology.modal.addTitle" : "chronology.modal.editTitle";
  const submitLabelId = mode === "add" ? "chronology.action.create" : "save";

  useEffect(() => {
    if (open) {
      setStep(EditorStep.EVENT_DETAILS);
    }
  }, [open]);

  const handleClose = () => {
    if (!isSaving) {
      onOpenChange(false);
      setStep(EditorStep.EVENT_DETAILS);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader className="space-y-1">
          <DialogTitle>
            <FormattedMessage id={titleId} />
          </DialogTitle>
          <DialogDescription asChild>
            <div>
              <span className="text-muted-foreground text-sm font-semibold leading-tight">
                {step === EditorStep.EVENT_DETAILS
                  ? intl.formatMessage({ id: "chronology.modal.step1" })
                  : intl.formatMessage({ id: "chronology.modal.step2" })}
              </span>
              <span className="text-muted-foreground text-sm font-normal leading-tight">
                :{" "}
                {step === EditorStep.EVENT_DETAILS
                  ? intl.formatMessage({
                      id: "chronology.modal.step1Description",
                    })
                  : intl.formatMessage({
                      id: "chronology.modal.step2Description",
                    })}
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>
        <ChronologyEventEditor
          initialValues={initialValues}
          isSaving={isSaving}
          isLoadingSuggestion={isLoadingSuggestion}
          submitLabelId={submitLabelId}
          onSubmit={onSubmit}
          onCancel={handleClose}
          step={step}
          onStepChange={setStep}
        />
      </DialogContent>
    </Dialog>
  );
};
